import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, ExternalLink } from "lucide-react";

const TrustpilotWidget = () => {
  useEffect(() => {
    // Load Trustpilot widget script
    const script = document.createElement("script");
    script.src = "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src*="trustpilot"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <section className="py-16 bg-secondary/5">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Client Reviews</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Trusted by Growing Businesses
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See what our clients say about working with Vincent Growth Partners.
            </p>
          </div>

          {/* Trustpilot Widget Container */}
          <div className="flex justify-center mb-8">
            <div 
              className="trustpilot-widget" 
              data-locale="en-GB" 
              data-template-id="56278e9abfbbba0bdcd568bc" 
              data-businessunit-id="YOUR_TRUSTPILOT_BUSINESS_ID" 
              data-style-height="52px" 
              data-style-width="100%"
              data-theme="light"
            >
              <a 
                href="https://uk.trustpilot.com/review/vincentgrowthpartners.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                View our Trustpilot reviews
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Fallback/Static Reviews */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "James Thompson",
                company: "Thompson Construction Ltd",
                rating: 5,
                review: "VGP transformed our online presence. We went from struggling to find quality leads to having a consistent pipeline of high-value projects."
              },
              {
                name: "Sarah Mitchell",
                company: "Elite Home Services",
                rating: 5,
                review: "Professional, results-driven, and genuinely invested in our success. The ROI we've seen has been exceptional."
              },
              {
                name: "David Chen",
                company: "Premier Landscaping",
                rating: 5,
                review: "Best decision we made for our business. The team understood our market and delivered exactly what they promised."
              }
            ].map((review, index) => (
              <Card key={index} className="border-border/50">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                    "{review.review}"
                  </p>
                  <div>
                    <p className="font-semibold text-foreground">{review.name}</p>
                    <p className="text-sm text-muted-foreground">{review.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <a 
              href="https://uk.trustpilot.com/review/vincentgrowthpartners.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-accent hover:text-accent/80 font-medium transition-colors"
            >
              View all reviews on Trustpilot
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustpilotWidget;
